/**
 * UserAdmin — Benutzerverwaltung mit 3 Tabs: Interne User, Portal-Kunden, Portal-MA.
 *
 * Features:
 * - Rollen-Badges mit Farbe/Icon aus roles_meta
 * - Portal-Kunden: ALLE Kunden mit Portal-Status (none/invited/active/disabled)
 * - User-Detail Slide-Over (Basis, Rechte, Verknüpfungen)
 * - Bulk-Einladung für Portal-Kunden
 *
 * @page /admin/users
 * @roles admin
 * @i18n userAdmin
 */
import { useState, useEffect, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Shield, ShieldCheck, Package, Handshake, BarChart3, Users, Eye, Building2, User, X, ChevronRight, Mail, Search, UserPlus, Check, Clock, AlertCircle, RefreshCw, LogIn, Trash2, UserCheck, KeyRound, Monitor, Link2, Settings, SlidersHorizontal, Plus, Pencil, MoreHorizontal, Ban, Power } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const api = (path, opts = {}) => fetch(`${API}${path}`, { credentials: 'include', ...opts }).then(r => r.json());
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPut = (path, body) => api(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

// Icon map for roles_meta.icon
const ROLE_ICONS = { Shield, ShieldCheck, Package, Handshake, BarChart3, Users, Eye, Building2, User };

function RoleBadge({ rolle, rolesMeta }) {
 const meta = rolesMeta.find(r => r.rolle === rolle);
 if (!meta) return <span className="text-[10px] text-[var(--text-tertiary)]">{rolle}</span>;
 const Icon = ROLE_ICONS[meta.icon];
 return (
 <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
 style={{ background: meta.color + '15', color: meta.color }}>
 {Icon && <Icon style={{ width: 12, height: 12 }} />}
 {meta.label_de}
 </span>
 );
}

function PortalStatusBadge({ status, t }) {
 const config = {
 active: { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: t('userAdmin.portalStatus.active') },
 invited: { dot: 'bg-primary-500', text: 'text-primary-600 dark:text-primary-400', label: t('userAdmin.portalStatus.invited') },
 expired: { dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', label: t('userAdmin.portalStatus.expired') },
 disabled: { dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400', label: t('userAdmin.portalStatus.disabled') },
 none: { dot: 'bg-[var(--text-muted,#9CA3AF)] dark:bg-[var(--text-secondary,#4B5563)]', text: 'text-[var(--text-tertiary)]', label: t('userAdmin.portalStatus.none') },
 };
 const c = config[status] || config.none;
 return (
 <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${c.text}`}>
 <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
 {c.label}
 </span>
 );
}

// ─── Overrides-Tab (im Slide-Over) ────────────────────────────

function OverridesTab({ userId, userRolle, rolesMeta, t, onReload }) {
 const { checkAuth, user } = useAuth();
 const [modules, setModules] = useState([]);
 const [basePerms, setBasePerms] = useState({}); // modul → 'full'/'none'
 const [overrides, setOverrides] = useState({}); // modul → true/false
 const [dirty, setDirty] = useState(false);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [feedback, setFeedback] = useState('');

 const load = async () => {
 setLoading(true);
 try {
 const [mods, rolePerms, userOvs] = await Promise.all([
 api('/api/modules'),
 api(`/api/roles/${userRolle}/permissions`),
 api(`/api/users/${userId}/overrides`),
 ]);
 setModules(Array.isArray(mods) ? mods : []);
 const base = {};
 for (const p of (Array.isArray(rolePerms) ? rolePerms : [])) base[p.modul] = p.permission;
 setBasePerms(base);
 const ovs = {};
 for (const o of (Array.isArray(userOvs) ? userOvs : [])) ovs[o.modul] = o.sichtbar;
 setOverrides(ovs);
 setDirty(false);
 } catch { /* ignore */ }
 setLoading(false);
 };

 useEffect(() => { load(); }, [userId, userRolle]);

 const setChoice = (modul, choice) => {
 setOverrides(o => {
 const next = { ...o };
 if (choice === 'follow') delete next[modul];
 else next[modul] = choice === 'grant';
 return next;
 });
 setDirty(true);
 };

 const save = async () => {
 setSaving(true);
 setFeedback('');
 try {
 const body = { overrides: Object.entries(overrides).map(([modul, sichtbar]) => ({ modul, sichtbar })) };
 const res = await apiPut(`/api/users/${userId}/overrides`, body);
 if (res.error) throw new Error(res.error);
 setFeedback(t('common.saved'));
 setDirty(false);
 onReload?.();
 if (user?.id === userId) await checkAuth();
 setTimeout(() => setFeedback(''), 2000);
 } catch (e) { setFeedback(e.message); }
 setSaving(false);
 };

 if (loading) return <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

 const overrideCount = Object.keys(overrides).length;

 return (
 <div className="space-y-3">
 <div className="flex items-start gap-2 bg-[var(--bg-inset)] rounded-lg px-3 py-2 border border-[var(--border-card)]">
 <SlidersHorizontal style={{ width: 12, height: 12 }} className="text-[var(--text-secondary)] flex-shrink-0 mt-0.5" />
 <p className="text-[11px] text-[var(--text-secondary)] leading-relaxed">{t('userAdmin.overrides.info')}</p>
 </div>

 <div className="space-y-1">
 {modules.map(m => {
 const baseFull = basePerms[m] === 'full';
 const hasOverride = m in overrides;
 const choice = !hasOverride ? 'follow' : (overrides[m] ? 'grant' : 'revoke');
 return (
 <div key={m} className={`flex items-center justify-between py-1.5 px-3 rounded-lg border ${hasOverride ? 'border-amber-500/30 bg-amber-500/5' : 'border-[var(--border-card)]'}`}>
 <div className="min-w-0 flex-1">
 <div className="text-xs text-[var(--text-primary)] capitalize">{m.replace(/_/g, ' ')}</div>
 <div className="text-[10px] text-[var(--text-tertiary)]">
 {t('userAdmin.overrides.roleBase')}: <span className={baseFull ? 'text-emerald-500' : 'text-red-500'}>{baseFull ? t('userAdmin.overrides.visible') : t('userAdmin.overrides.hidden')}</span>
 </div>
 </div>
 <select value={choice} onChange={e => setChoice(m, e.target.value)}
 className={`bg-[var(--bg-inset)] border rounded px-2 py-1 text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50 ${hasOverride ? 'border-amber-500/40' : 'border-[var(--border-card)]'}`}>
 <option value="follow">{t('userAdmin.overrides.follow')}</option>
 <option value="grant">{t('userAdmin.overrides.grant')}</option>
 <option value="revoke">{t('userAdmin.overrides.revoke')}</option>
 </select>
 </div>
 );
 })}
 </div>

 <div className="flex items-center justify-between pt-2">
 <span className="text-[11px] text-[var(--text-tertiary)]">
 {overrideCount === 0 ? t('userAdmin.overrides.none') : t('userAdmin.overrides.count', { count: overrideCount })}
 </span>
 <div className="flex items-center gap-2">
 {feedback && <span className="text-[11px] text-emerald-500">{feedback}</span>}
 <button onClick={save} disabled={!dirty || saving}
 className="bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium px-3 py-1.5 rounded-lg">
 {saving ? '...' : t('common.save')}
 </button>
 </div>
 </div>
 </div>
 );
}

// ─── Slide-Over ───────────────────────────────────────────────

function UserDetailSlideOver({ user, rolesMeta, permissions, standorte, onClose, onUpdate, t }) {
 const [detailTab, setDetailTab] = useState('basis');
 const [rolleValue, setRolleValue] = useState(user.rolle);
 const [partnerStandortId, setPartnerStandortId] = useState(user.partner_standort_id || '');
 const [saving, setSaving] = useState(false);
 const [pwValue, setPwValue] = useState('');
 const [pwSaving, setPwSaving] = useState(false);
 const [feedback, setFeedback] = useState('');

 const internalRoles = rolesMeta.filter(r => r.is_internal);
 const roleMeta = rolesMeta.find(r => r.rolle === user.rolle);
 const permList = permissions || [];

 const handleRoleChange = async (newRolle) => {
 setSaving(true);
 setFeedback('');
 try {
 const res = await apiPut(`/api/users/${user.id}/rolle`, { rolle: newRolle });
 if (res.error) throw new Error(res.error);
 setRolleValue(newRolle);
 setFeedback(t('userAdmin.roleSaved'));
 onUpdate();
 } catch (err) { setFeedback(err.message); }
 setSaving(false);
 };

 const handlePartnerStandort = async (val) => {
 setSaving(true);
 setFeedback('');
 try {
 const res = await apiPut(`/api/users/${user.id}`, { partner_standort_id: val ? Number(val) : null });
 if (res.error) throw new Error(res.error);
 setPartnerStandortId(val);
 setFeedback(t('common.saved'));
 onUpdate();
 } catch (err) { setFeedback(err.message); }
 setSaving(false);
 };

 const handleResetPassword = async () => {
 if (!pwValue || pwValue.length < 6) return setFeedback(t('userAdmin.pwMinLength'));
 setPwSaving(true);
 setFeedback('');
 try {
 const res = await apiPut(`/api/users/${user.id}`, { password: pwValue });
 if (res.error) throw new Error(res.error);
 setPwValue('');
 setFeedback(t('userAdmin.pwSaved'));
 } catch (err) { setFeedback(err.message); }
 setPwSaving(false);
 };

 const handleToggleActive = async () => {
 try {
 await apiPut(`/api/users/${user.id}`, { aktiv: !user.aktiv });
 onUpdate();
 onClose();
 } catch { /* ignore */ }
 };

 const tabs = [
 { key: 'basis', label: t('userAdmin.detail.basis') },
 { key: 'rechte', label: t('userAdmin.detail.permissions') },
 { key: 'overrides', label: t('userAdmin.detail.overrides') },
 { key: 'verknuepfungen', label: t('userAdmin.detail.connections') },
 ];

 return (
 <div className="fixed inset-0 z-50">
 <div className="absolute inset-0 bg-black/30" onClick={onClose} />
 <div className="absolute right-0 top-0 bottom-0 w-full max-w-[480px] bg-[var(--bg-card)] border-l border-[var(--border-card)] overflow-y-auto flex flex-col">
 {/* Header */}
 <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-card)] flex-shrink-0">
 <div className="flex items-center gap-3">
 <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${['portal_kunde'].includes(user.rolle) ? 'bg-gradient-to-br from-cyan-500 to-primary-600' : 'bg-gradient-to-br from-primary-500 to-purple-600'}`}>
 <span className="text-xs font-bold text-white">{(user.vorname?.[0] || '') + (user.nachname?.[0] || '')}</span>
 </div>
 <div>
 <div className="text-sm font-semibold text-[var(--text-primary)]">{user.vorname} {user.nachname}</div>
 <div className="text-[11px] text-[var(--text-secondary)]">{user.email}</div>
 </div>
 </div>
 <button onClick={onClose} className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-inset)] transition-colors">
 <X style={{ width: 16, height: 16 }} />
 </button>
 </div>

 {/* Tabs */}
 <div className="flex gap-0 border-b border-[var(--border-card)] px-5 flex-shrink-0">
 {tabs.map(tb => (
 <button key={tb.key} onClick={() => setDetailTab(tb.key)}
 className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${detailTab === tb.key ? 'border-[#338edc] text-[var(--text-primary)]' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
 {tb.label}
 </button>
 ))}
 </div>

 {/* Content */}
 <div className="flex-1 overflow-y-auto p-5 space-y-4">
 {feedback && (
 <div className={`text-xs px-3 py-2 rounded-lg border ${feedback.includes('Error') || feedback.includes('Fehler') || feedback.includes('min') ? 'bg-red-500/10 border-red-500/20 text-red-500' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'}`}>
 {feedback}
 </div>
 )}

 {detailTab === 'basis' && (
 <>
 {/* Role */}
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">{t('userAdmin.roleHeader')}</label>
 <div className="flex items-center gap-2">
 <RoleBadge rolle={rolleValue} rolesMeta={rolesMeta} />
 </div>
 <select
 value={rolleValue}
 onChange={e => handleRoleChange(e.target.value)}
 disabled={saving}
 className="mt-2 w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50 focus:ring-1 focus:ring-[#338edc]/30"
 >
 {internalRoles.map(r => (
 <option key={r.rolle} value={r.rolle}>{r.label_de}</option>
 ))}
 </select>
 </div>

 {/* Partner-Standort (only for jv_partner) */}
 {rolleValue === 'jv_partner' && (
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">{t('userAdmin.partnerStandort')}</label>
 <select
 value={partnerStandortId || ''}
 onChange={e => handlePartnerStandort(e.target.value)}
 disabled={saving}
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50 focus:ring-1 focus:ring-[#338edc]/30"
 >
 <option value="">{t('userAdmin.noStandort')}</option>
 {(standorte || []).map(s => (
 <option key={s.id} value={s.id}>{s.name}{s.betriebsmodell === 'joint_venture' ? ` (JV)` : ''}</option>
 ))}
 </select>
 </div>
 )}

 {/* Status */}
 <div className="flex items-center justify-between p-3 bg-[var(--bg-inset)] rounded-lg border border-[var(--border-card)]">
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">{t('userAdmin.status')}</div>
 <div className={`text-[11px] font-medium mt-0.5 ${user.aktiv ? 'text-emerald-500' : 'text-red-500'}`}>
 {user.aktiv ? t('userAdmin.statusActive') : t('userAdmin.statusDisabled')}
 </div>
 </div>
 <button onClick={handleToggleActive}
 className={`text-[10px] font-medium px-2.5 py-1.5 rounded-lg border transition-colors ${user.aktiv ? 'text-red-500 border-red-500/20 hover:bg-red-500/10' : 'text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/10'}`}>
 {user.aktiv ? t('userAdmin.deactivate') : t('userAdmin.activate')}
 </button>
 </div>

 {/* Info */}
 <div className="space-y-2">
 <div className="flex justify-between text-xs">
 <span className="text-[var(--text-secondary)]">{t('userAdmin.lastLogin')}</span>
 <span className="text-[var(--text-primary)]">{user.letzter_login ? new Date(user.letzter_login).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : t('userAdmin.never')}</span>
 </div>
 <div className="flex justify-between text-xs">
 <span className="text-[var(--text-secondary)]">{t('userAdmin.created')}</span>
 <span className="text-[var(--text-primary)]">{new Date(user.created_at).toLocaleDateString('de-AT')}</span>
 </div>
 </div>

 {/* Password Reset */}
 <div className="border-t border-[var(--border-card)] pt-4">
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">{t('userAdmin.detail.resetPassword')}</label>
 <div className="flex gap-2">
 <input type="password" value={pwValue} onChange={e => setPwValue(e.target.value)}
 className="flex-1 bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50"
 placeholder={t('userAdmin.newPassword')} autoComplete="new-password" />
 <button onClick={handleResetPassword} disabled={pwSaving || !pwValue}
 className="bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-40">
 {pwSaving ? '...' : t('common.save')}
 </button>
 </div>
 </div>
 </>
 )}

 {detailTab === 'rechte' && (
 <>
 <p className="text-[11px] text-[var(--text-secondary)] mb-3">
 {t('userAdmin.permissionsInfo')}
 </p>
 <div className="space-y-1">
 {permList.map(p => (
 <div key={p.modul} className="flex items-center justify-between py-2 px-3 rounded-lg border border-[var(--border-card)]">
 <span className="text-xs text-[var(--text-primary)] capitalize">{p.modul.replace(/_/g, ' ')}</span>
 <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
 p.permission === 'full' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
 p.permission === 'read' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
 p.permission === 'team' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' :
 'bg-[rgba(107,114,128,0.1)] text-[var(--text-tertiary)]'
 }`}>
 {p.permission}
 </span>
 </div>
 ))}
 </div>
 </>
 )}

 {detailTab === 'overrides' && (
 <OverridesTab userId={user.id} userRolle={user.rolle} rolesMeta={rolesMeta} t={t} onReload={onUpdate} />
 )}

 {detailTab === 'verknuepfungen' && (
 <div className="space-y-3">
 <div className="p-3 bg-[var(--bg-inset)] rounded-lg border border-[var(--border-card)]">
 <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.employeeLink')}</div>
 <div className="text-xs text-[var(--text-primary)]">
 {user.mitarbeiter_name
 ? <span className="inline-flex items-center gap-1 text-emerald-500"><Check style={{ width: 12, height: 12 }} /> {user.mitarbeiter_name}</span>
 : <span className="text-[var(--text-tertiary)]">{t('userAdmin.notLinked')}</span>}
 </div>
 </div>
 <div className="p-3 bg-[var(--bg-inset)] rounded-lg border border-[var(--border-card)]">
 <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.customerLink')}</div>
 <div className="text-xs text-[var(--text-primary)]">
 {user.kunde_name
 ? <span className="inline-flex items-center gap-1 text-emerald-500"><Check style={{ width: 12, height: 12 }} /> {user.kunde_name}</span>
 : <span className="text-[var(--text-tertiary)]">{t('userAdmin.notLinked')}</span>}
 </div>
 </div>
 {user.dashboard_rollen?.length > 0 && (
 <div className="p-3 bg-[var(--bg-inset)] rounded-lg border border-[var(--border-card)]">
 <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.dashboardRoles')}</div>
 <div className="flex gap-1.5 mt-1">
 {user.dashboard_rollen.map(r => (
 <span key={r} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-500 uppercase">{r}</span>
 ))}
 </div>
 </div>
 )}
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

// ─── Invite Modal ─────────────────────────────────────────────

function InviteModal({ open, onClose, tab, kunden, presetKunde, rolesMeta, onSuccess, t }) {
 const [email, setEmail] = useState('');
 const [rolle, setRolle] = useState(tab === 'portal_kunden' ? 'portal_kunde' : 'ops_manager');
 const [kundeId, setKundeId] = useState('');
 const [error, setError] = useState('');
 const [success, setSuccess] = useState('');
 const [inviteUrl, setInviteUrl] = useState('');
 const [emailSent, setEmailSent] = useState(null);
 const [copied, setCopied] = useState(false);
 const [loading, setLoading] = useState(false);
 const [lastPresetId, setLastPresetId] = useState(null);

 // Pre-fill when opening with a specific customer
 if (open && presetKunde && presetKunde.id !== lastPresetId) {
 setLastPresetId(presetKunde.id);
 setKundeId(String(presetKunde.id));
 setEmail(presetKunde.kontakt_email || '');
 setError('');
 setSuccess('');
 setInviteUrl('');
 setEmailSent(null);
 setCopied(false);
 }
 // Reset when opening without preset (header button)
 if (open && !presetKunde && lastPresetId !== null) {
 setLastPresetId(null);
 setKundeId('');
 setEmail('');
 setError('');
 setSuccess('');
 setInviteUrl('');
 setEmailSent(null);
 setCopied(false);
 }

 if (!open) return null;

 const internalRoles = rolesMeta.filter(r => r.is_internal);
 const isPortal = tab === 'portal_kunden';

 const handleSubmit = async (e) => {
 e.preventDefault();
 setError(''); setSuccess(''); setInviteUrl(''); setEmailSent(null); setLoading(true);
 try {
 const body = { email, rolle: isPortal ? 'portal_kunde' : rolle };
 if (isPortal && kundeId) body.kunde_id = Number(kundeId);
 const result = await apiPost('/api/einladungen', body);
 if (result.error) throw new Error(result.error);
 setSuccess(t('userAdmin.inviteSentTo', { email }) || `Einladung erstellt für ${email}`);
 setInviteUrl(result.invite_url || '');
 setEmailSent(result.email_sent);
 onSuccess();
 } catch (err) { setError(err.message); }
 setLoading(false);
 };

 const copyInviteUrl = async () => {
 try {
 await navigator.clipboard.writeText(inviteUrl);
 setCopied(true);
 setTimeout(() => setCopied(false), 2000);
 } catch { /* ignore */ }
 };

 const handleClose = () => {
 setEmail(''); setError(''); setSuccess(''); setInviteUrl(''); setEmailSent(null); setCopied(false);
 onClose();
 };

 const modalTitle = presetKunde
 ? t('userAdmin.inviteCustomer') + ': ' + presetKunde.name
 : isPortal ? t('userAdmin.inviteCustomer') : t('userAdmin.inviteUser');

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={handleClose}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{modalTitle}</h3>
 <button onClick={handleClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <form onSubmit={handleSubmit} className="p-5 space-y-3">
 {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-500">{error}</div>}
 {success && (
 <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
 <div className="flex items-center gap-1.5"><Check style={{ width: 12, height: 12 }} /> {success}</div>
 {emailSent === false && (
 <div className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">{t('userAdmin.inviteEmailFailed') || 'E-Mail-Versand fehlgeschlagen — bitte Link manuell senden.'}</div>
 )}
 {emailSent === true && (
 <div className="mt-1 text-[11px] text-[var(--text-secondary)]">{t('userAdmin.inviteEmailSent') || 'E-Mail wurde versendet.'}</div>
 )}
 </div>
 )}

 {inviteUrl ? (
 <div className="space-y-2">
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.inviteLink') || 'Einladungslink'}</label>
 <div className="flex gap-2">
 <input type="text" readOnly value={inviteUrl}
 className="flex-1 bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-[11px] text-[var(--text-primary)] font-mono" />
 <button type="button" onClick={copyInviteUrl}
 className="bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium px-3 py-2 rounded-lg whitespace-nowrap">
 {copied ? (t('common.copied') || 'Kopiert!') : (t('common.copy') || 'Kopieren')}
 </button>
 </div>
 <button type="button" onClick={handleClose}
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] text-xs font-medium py-2 rounded-lg">
 {t('common.close') || 'Schließen'}
 </button>
 </div>
 ) : (
 <>
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">E-Mail</label>
 <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50 focus:ring-1 focus:ring-[#338edc]/30"
 placeholder="name@firma.at" />
 </div>

 {!isPortal && (
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.roleHeader')}</label>
 <select value={rolle} onChange={e => setRolle(e.target.value)}
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50">
 {internalRoles.map(r => <option key={r.rolle} value={r.rolle}>{r.label_de}</option>)}
 </select>
 </div>
 )}

 {isPortal && !presetKunde && (
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.assignCustomer')}</label>
 <select value={kundeId} onChange={e => setKundeId(e.target.value)} required
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50">
 <option value="">{t('userAdmin.pleaseSelect')}</option>
 {kunden.filter(k => k.portal_status === 'none').map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
 </select>
 </div>
 )}

 <button type="submit" disabled={loading}
 className="w-full bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50">
 {loading ? '...' : t('userAdmin.sendInvite')}
 </button>
 </>
 )}
 </form>
 </div>
 </div>
 );
}

// ─── Rolle Form Modal ────────────────────────────────────────

function RoleFormModal({ open, onClose, onSaved, role, t }) {
 const [form, setForm] = useState({ rolle: '', label_de: '', label_en: '', color: '#338edc', icon: 'User', is_internal: true });
 const [saving, setSaving] = useState(false);
 const [err, setErr] = useState('');

 useEffect(() => {
 if (role) setForm({ rolle: role.rolle, label_de: role.label_de || '', label_en: role.label_en || '', color: role.color || '#338edc', icon: role.icon || 'User', is_internal: role.is_internal !== false });
 else setForm({ rolle: '', label_de: '', label_en: '', color: '#338edc', icon: 'User', is_internal: true });
 setErr('');
 }, [role, open]);

 if (!open) return null;
 const isEdit = !!role;

 const save = async (e) => {
 e.preventDefault();
 setSaving(true);
 setErr('');
 try {
 const body = { label_de: form.label_de, label_en: form.label_en, color: form.color, icon: form.icon, is_internal: form.is_internal };
 const res = isEdit
 ? await apiPut(`/api/roles/${role.rolle}`, body)
 : await apiPost('/api/roles', { rolle: form.rolle, ...body });
 if (res.error) throw new Error(res.error);
 onSaved();
 onClose();
 } catch (e) { setErr(e.message); }
 setSaving(false);
 };

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !saving && onClose()}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{isEdit ? t('userAdmin.roles.edit') : t('userAdmin.roles.new')}</h3>
 <button onClick={onClose} disabled={saving} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <form onSubmit={save} className="p-5 space-y-3">
 {err && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-500">{err}</div>}
 {!isEdit && (
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.roles.key')}</label>
 <input type="text" value={form.rolle} onChange={e => setForm(f => ({ ...f, rolle: e.target.value.toLowerCase() }))} required pattern="[a-z_][a-z0-9_]*"
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50"
 placeholder="z.B. sales_manager" />
 <p className="text-[10px] text-[var(--text-tertiary)] mt-1">{t('userAdmin.roles.keyHint')}</p>
 </div>
 )}
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Label DE</label>
 <input type="text" value={form.label_de} onChange={e => setForm(f => ({ ...f, label_de: e.target.value }))} required
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50" />
 </div>
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">Label EN</label>
 <input type="text" value={form.label_en} onChange={e => setForm(f => ({ ...f, label_en: e.target.value }))} required
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50" />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.roles.color')}</label>
 <div className="flex items-center gap-2">
 <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
 className="h-8 w-12 rounded border border-[var(--border-card)] bg-transparent cursor-pointer" />
 <input type="text" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
 className="flex-1 bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-2 py-1.5 text-[11px] text-[var(--text-primary)] font-mono" />
 </div>
 </div>
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.roles.icon')}</label>
 <select value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)]">
 {Object.keys(ROLE_ICONS).map(k => <option key={k} value={k}>{k}</option>)}
 </select>
 </div>
 </div>
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="checkbox" checked={form.is_internal} onChange={e => setForm(f => ({ ...f, is_internal: e.target.checked }))}
 className="w-3.5 h-3.5 rounded border-[var(--border-card)] text-[#338edc]" />
 <span className="text-xs text-[var(--text-secondary)]">{t('userAdmin.roles.internal')}</span>
 </label>
 <div className="flex justify-end gap-2 pt-2">
 <button type="button" onClick={onClose} disabled={saving}
 className="px-4 py-2 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]">{t('common.cancel')}</button>
 <button type="submit" disabled={saving}
 className="bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium px-4 py-2 rounded-lg disabled:opacity-50">
 {saving ? '...' : t('common.save')}
 </button>
 </div>
 </form>
 </div>
 </div>
 );
}

// ─── Rollen-Tab ───────────────────────────────────────────────

function RolesTab({ roles, users, onReload, t }) {
 const [editRole, setEditRole] = useState(null);
 const [newModal, setNewModal] = useState(false);
 const [deleteConfirm, setDeleteConfirm] = useState(null);
 const [deleting, setDeleting] = useState(false);

 const userCountByRole = useMemo(() => {
 const map = {};
 for (const u of users) map[u.rolle] = (map[u.rolle] || 0) + 1;
 return map;
 }, [users]);

 const handleDelete = async () => {
 if (!deleteConfirm) return;
 setDeleting(true);
 try {
 const res = await fetch(`${API}/api/roles/${deleteConfirm.rolle}`, { method: 'DELETE', credentials: 'include' }).then(r => r.json());
 if (res.error) throw new Error(res.error);
 onReload();
 setDeleteConfirm(null);
 } catch (e) { alert(e.message); }
 setDeleting(false);
 };

 return (
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <p className="text-xs text-[var(--text-secondary)]">{t('userAdmin.roles.intro', { count: roles.length })}</p>
 <button onClick={() => setNewModal(true)}
 className="bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5">
 <Plus style={{ width: 14, height: 14 }} /> {t('userAdmin.roles.new')}
 </button>
 </div>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
 {roles.map(r => {
 const Icon = ROLE_ICONS[r.icon] || User;
 const count = userCountByRole[r.rolle] || 0;
 return (
 <div key={r.rolle} className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl p-4 flex flex-col gap-3">
 <div className="flex items-center gap-3">
 <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: r.color + '15', color: r.color }}>
 <Icon style={{ width: 16, height: 16 }} />
 </span>
 <div className="flex-1 min-w-0">
 <div className="text-sm font-semibold text-[var(--text-primary)] truncate">{r.label_de}</div>
 <div className="text-[11px] text-[var(--text-tertiary)] font-mono truncate">{r.rolle}</div>
 </div>
 </div>
 <div className="flex items-center justify-between text-[11px] text-[var(--text-secondary)]">
 <span>{count} {count === 1 ? t('userAdmin.roles.userSingular') : t('userAdmin.roles.userPlural')}</span>
 <span className="flex items-center gap-1.5">
 {r.is_internal ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset)] border border-[var(--border-card)]">{t('userAdmin.roles.internal')}</span>
 : <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset)] border border-[var(--border-card)]">{t('userAdmin.roles.external')}</span>}
 </span>
 </div>
 <div className="flex items-center justify-end gap-1 pt-1 border-t border-[var(--border-card)]">
 {r.editable === false ? (
 <span className="text-[10px] text-[var(--text-tertiary)] italic">{t('userAdmin.roles.locked')}</span>
 ) : (
 <>
 <button onClick={() => setEditRole(r)}
 className="text-[11px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-2 py-1 rounded inline-flex items-center gap-1">
 <Pencil style={{ width: 11, height: 11 }} /> {t('common.edit')}
 </button>
 <button onClick={() => setDeleteConfirm({ rolle: r.rolle, label: r.label_de, count })}
 className="text-[11px] font-medium text-red-500 hover:text-red-600 px-2 py-1 rounded inline-flex items-center gap-1">
 <Trash2 style={{ width: 11, height: 11 }} /> {t('common.delete')}
 </button>
 </>
 )}
 </div>
 </div>
 );
 })}
 </div>

 <RoleFormModal open={newModal} onClose={() => setNewModal(false)} onSaved={onReload} role={null} t={t} />
 <RoleFormModal open={!!editRole} onClose={() => setEditRole(null)} onSaved={onReload} role={editRole} t={t} />

 {deleteConfirm && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !deleting && setDeleteConfirm(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('userAdmin.roles.deleteTitle')}</h3>
 <button onClick={() => setDeleteConfirm(null)} disabled={deleting} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <div className="p-5 space-y-3">
 <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
 {t('userAdmin.roles.deleteConfirm', { label: deleteConfirm.label })}
 </p>
 {deleteConfirm.count > 0 && (
 <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
 <AlertCircle style={{ width: 14, height: 14 }} className="text-amber-500 flex-shrink-0 mt-0.5" />
 <span className="text-[11px] text-amber-600 dark:text-amber-400">{t('userAdmin.roles.deleteWarn', { count: deleteConfirm.count })}</span>
 </div>
 )}
 <div className="flex justify-end gap-2 pt-1">
 <button onClick={() => setDeleteConfirm(null)} disabled={deleting}
 className="px-4 py-2 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]">{t('common.cancel')}</button>
 <button onClick={handleDelete} disabled={deleting}
 className="bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-4 py-2 rounded-lg disabled:opacity-50">
 {deleting ? '...' : t('common.delete')}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ─── Permissions-Matrix-Tab ───────────────────────────────────

function PermissionsMatrixTab({ roles, t }) {
 const { checkAuth, user } = useAuth();
 const [modules, setModules] = useState([]);
 const [matrix, setMatrix] = useState({}); // rolle → { modul: boolean }
 const [dirty, setDirty] = useState({}); // rolle → true
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [savedFeedback, setSavedFeedback] = useState('');

 const loadAll = async () => {
 setLoading(true);
 try {
 const mods = await api('/api/modules');
 setModules(Array.isArray(mods) ? mods : []);
 const next = {};
 for (const r of roles) {
 const perms = await api(`/api/roles/${r.rolle}/permissions`);
 next[r.rolle] = {};
 for (const p of perms) next[r.rolle][p.modul] = p.permission === 'full';
 for (const m of mods) if (!(m in next[r.rolle])) next[r.rolle][m] = false;
 }
 setMatrix(next);
 setDirty({});
 } catch { /* ignore */ }
 setLoading(false);
 };

 useEffect(() => { if (roles.length) loadAll(); }, [roles.length]);

 const toggle = (rolle, modul) => {
 setMatrix(m => ({ ...m, [rolle]: { ...m[rolle], [modul]: !m[rolle]?.[modul] } }));
 setDirty(d => ({ ...d, [rolle]: true }));
 };

 const save = async () => {
 setSaving(true);
 try {
 const changedRoles = Object.keys(dirty);
 for (const rolle of changedRoles) {
 await apiPut(`/api/roles/${rolle}/permissions`, { permissions: matrix[rolle] });
 }
 setDirty({});
 setSavedFeedback(t('userAdmin.permissionsMatrix.saved'));
 setTimeout(() => setSavedFeedback(''), 2500);
 if (user?.rolle && changedRoles.includes(user.rolle)) await checkAuth();
 } catch (e) { setSavedFeedback(e.message); }
 setSaving(false);
 };

 const dirtyCount = Object.keys(dirty).length;

 if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

 return (
 <div className="space-y-3">
 <div className="flex items-center justify-between gap-3 flex-wrap">
 <p className="text-xs text-[var(--text-secondary)]">{t('userAdmin.permissionsMatrix.intro')}</p>
 <div className="flex items-center gap-3">
 {savedFeedback && <span className="text-[11px] text-emerald-500">{savedFeedback}</span>}
 <button onClick={save} disabled={saving || dirtyCount === 0}
 className="bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium px-3.5 py-2 rounded-lg flex items-center gap-1.5">
 {saving ? '...' : `${t('common.save')}${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
 </button>
 </div>
 </div>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl overflow-auto max-h-[70vh]">
 <table className="text-sm border-separate border-spacing-0">
 <thead className="sticky top-0 z-20">
 <tr className="bg-[var(--bg-card)]">
 <th className="sticky left-0 z-30 bg-[var(--bg-card)] text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider border-b border-r border-[var(--border-card)] min-w-[180px]">
 {t('userAdmin.permissionsMatrix.role')}
 </th>
 {modules.map(m => (
 <th key={m} className="text-center px-2 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider border-b border-[var(--border-card)] whitespace-nowrap">
 {m}
 </th>
 ))}
 </tr>
 </thead>
 <tbody>
 {roles.map(r => {
 const Icon = ROLE_ICONS[r.icon] || User;
 return (
 <tr key={r.rolle} className={`${dirty[r.rolle] ? 'bg-amber-500/5' : ''}`}>
 <td className="sticky left-0 z-10 bg-[var(--bg-card)] px-4 py-2 border-b border-r border-[var(--border-card)]">
 <div className="flex items-center gap-2">
 <span className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ background: r.color + '15', color: r.color }}>
 <Icon style={{ width: 12, height: 12 }} />
 </span>
 <span className="text-xs font-medium text-[var(--text-primary)]">{r.label_de}</span>
 </div>
 </td>
 {modules.map(m => (
 <td key={m} className="text-center px-2 py-2 border-b border-[var(--border-card)]">
 <input
 type="checkbox"
 checked={!!matrix[r.rolle]?.[m]}
 onChange={() => toggle(r.rolle, m)}
 className="w-3.5 h-3.5 rounded border-[var(--border-card)] text-[#338edc] focus:ring-[#338edc]/30 cursor-pointer"
 />
 </td>
 ))}
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 </div>
 );
}

// ─── Main Component ───────────────────────────────────────────

export default function UserAdmin() {
 const { t } = useLanguage();
 const { checkAuth } = useAuth();
 const navigate = useNavigate();
 const [tab, setTab] = useTabParam('intern');
 const [users, setUsers] = useState([]);
 const [kunden, setKunden] = useState([]);
 const [roles, setRoles] = useState([]);
 const [loading, setLoading] = useState(true);
 const [selectedUser, setSelectedUser] = useState(null);
 const [selectedPerms, setSelectedPerms] = useState([]);
 const [inviteModal, setInviteModal] = useState(null); // null=closed, {}=header, {kunde}=row
 const [search, setSearch] = useState('');
 const [statusFilter, setStatusFilter] = useState('all');
 const [segmentFilter, setSegmentFilter] = useState('all');
 const [selectedKunden, setSelectedKunden] = useState(new Set());
 const [bulkInviting, setBulkInviting] = useState(false);
 const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
 const [bulkResult, setBulkResult] = useState(null);
 const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, email, kundeName }
 const [deleting, setDeleting] = useState(false);
 const [standorte, setStandorte] = useState([]);
 const [maData, setMaData] = useState({ mitarbeiter: [], summary: {} });
 const [maFilter, setMaFilter] = useState('all'); // all | with | without
 const [createModal, setCreateModal] = useState(null); // null | { ma }
 const [createForm, setCreateForm] = useState({ email: '', rolle: 'portal_mitarbeiter', password: '', generate_pin: true });
 const [createLoading, setCreateLoading] = useState(false);
 const [createError, setCreateError] = useState('');
 const [maVerfuegbar, setMaVerfuegbar] = useState([]);
 const [linkDropdown, setLinkDropdown] = useState(null);
 const [usersWithOverrides, setUsersWithOverrides] = useState(new Set());
 const [pendingInvites, setPendingInvites] = useState([]);
 const [internStatusFilter, setInternStatusFilter] = useState('active_invited'); // active_invited | all | active | invited | disabled
 const [actionMenu, setActionMenu] = useState(null); // { id, type: 'user'|'invite' }
 const [editUser, setEditUser] = useState(null);
 const [deleteUserConfirm, setDeleteUserConfirm] = useState(null); // { id, email, vorname, nachname, hard }
 const [deleteInviteConfirm, setDeleteInviteConfirm] = useState(null); // { id, email }
 const [pwResetTarget, setPwResetTarget] = useState(null); // { id, email }
 const [actionFeedback, setActionFeedback] = useState('');

 const load = async () => {
 try {
 const [u, r, k, s, ma, maVerf, ov, inv] = await Promise.all([
 api('/api/users'),
 api('/api/roles'),
 api('/api/admin/kunden-portal-status'),
 api('/api/standorte'),
 api('/api/admin/user-ma-uebersicht'),
 api('/api/admin/mitarbeiter/verfuegbar'),
 api('/api/users/with-overrides').catch(() => []),
 api('/api/einladungen').catch(() => []),
 ]);
 setUsers(Array.isArray(u) ? u : []);
 setRoles(Array.isArray(r) ? r : []);
 setKunden(Array.isArray(k) ? k : []);
 setStandorte(Array.isArray(s) ? s : []);
 if (ma?.mitarbeiter) setMaData(ma);
 setMaVerfuegbar(Array.isArray(maVerf) ? maVerf : []);
 setUsersWithOverrides(new Set(Array.isArray(ov) ? ov.map(x => (typeof x === 'object' ? x.user_id : x)) : []));
 setPendingInvites(Array.isArray(inv) ? inv.filter(i => !i.angenommen) : []);
 } catch { /* ignore */ }
 finally { setLoading(false); }
 };

 const showFeedback = (msg) => { setActionFeedback(msg); setTimeout(() => setActionFeedback(''), 3000); };

 useEffect(() => { load(); }, []);

 const handleImpersonate = async (userId) => {
 try {
 const res = await fetch(`${API}/api/admin/impersonate/${userId}`, { method: 'POST', credentials: 'include' });
 const data = await res.json();
 if (res.ok) {
 await checkAuth();
 navigate(data.redirect || '/portal');
 } else {
 alert(data.error || 'Impersonate fehlgeschlagen');
 }
 } catch { alert('Netzwerkfehler'); }
 };

 const handleUserClick = async (user) => {
 setSelectedUser(user);
 try {
 const perms = await api(`/api/roles/${user.rolle}/permissions`);
 setSelectedPerms(Array.isArray(perms) ? perms : []);
 } catch { setSelectedPerms([]); }
 };

 const handleDeletePortalUser = async () => {
 if (!deleteConfirm) return;
 setDeleting(true);
 try {
 const res = await fetch(`${API}/api/users/${deleteConfirm.id}`, { method: 'DELETE', credentials: 'include' });
 if (!res.ok) throw new Error('Delete failed');
 setDeleteConfirm(null);
 load();
 } catch { /* ignore */ }
 finally { setDeleting(false); }
 };

 // ─── #981 User-CRUD Aktionen (intern) ───
 const handleToggleAktiv = async (u) => {
 try {
 const res = u.aktiv
 ? await fetch(`${API}/api/users/${u.id}`, { method: 'DELETE', credentials: 'include' }).then(r => r.json())
 : await apiPut(`/api/users/${u.id}`, { aktiv: true });
 if (res.error) throw new Error(res.error);
 showFeedback(u.aktiv ? t('userAdmin.feedback.deactivated') || 'User deaktiviert' : t('userAdmin.feedback.activated') || 'User aktiviert');
 setActionMenu(null);
 load();
 } catch (err) { showFeedback(err.message); }
 };

 const handleDeleteUserHard = async () => {
 if (!deleteUserConfirm) return;
 try {
 const res = await fetch(`${API}/api/users/${deleteUserConfirm.id}/hard`, { method: 'DELETE', credentials: 'include' }).then(r => r.json());
 if (res.error) throw new Error(res.error);
 showFeedback(t('userAdmin.feedback.userDeleted') || 'User endgültig gelöscht');
 setDeleteUserConfirm(null);
 load();
 } catch (err) { showFeedback(err.message); setDeleteUserConfirm(null); }
 };

 const handleResetPassword = async () => {
 if (!pwResetTarget?.password || pwResetTarget.password.length < 6) {
 showFeedback(t('userAdmin.feedback.pwMinLength') || 'Passwort muss mind. 6 Zeichen haben');
 return;
 }
 try {
 const res = await apiPost(`/api/admin/user/${pwResetTarget.id}/passwort-reset`, { password: pwResetTarget.password });
 if (res.error) throw new Error(res.error);
 showFeedback(t('userAdmin.feedback.pwReset') || 'Passwort zurückgesetzt');
 setPwResetTarget(null);
 } catch (err) { showFeedback(err.message); }
 };

 const handleResendInvite = async (inviteId) => {
 try {
 const res = await apiPost(`/api/einladungen/${inviteId}/resend`);
 if (res.error) throw new Error(res.error);
 showFeedback(res.sent ? (t('userAdmin.feedback.inviteResent') || 'Einladung erneut gesendet') : (t('userAdmin.feedback.inviteResendFailed') || 'Versand fehlgeschlagen'));
 setActionMenu(null);
 load();
 } catch (err) { showFeedback(err.message); }
 };

 const handleDeleteInvite = async () => {
 if (!deleteInviteConfirm) return;
 try {
 const res = await fetch(`${API}/api/einladungen/${deleteInviteConfirm.id}`, { method: 'DELETE', credentials: 'include' }).then(r => r.json());
 if (res.error) throw new Error(res.error);
 showFeedback(t('userAdmin.feedback.inviteRevoked') || 'Einladung widerrufen');
 setDeleteInviteConfirm(null);
 load();
 } catch (err) { showFeedback(err.message); setDeleteInviteConfirm(null); }
 };

 // Click-outside handler for action menu
 useEffect(() => {
 if (!actionMenu) return;
 const handler = () => setActionMenu(null);
 document.addEventListener('click', handler);
 return () => document.removeEventListener('click', handler);
 }, [actionMenu]);

 // ─── Filtered data ───

 const isInternalRole = (rolle) => {
 const meta = roles.find(r => r.rolle === rolle);
 return meta ? meta.is_internal : !['portal_kunde', 'portal_mitarbeiter', 'kunde', 'mitarbeiter'].includes(rolle);
 };

 // #981: Status pro User berechnen (active/invited/disabled/expired)
 const internalRows = useMemo(() => {
 const userRows = users
 .filter(u => isInternalRole(u.rolle))
 .map(u => ({ ...u, _kind: 'user', _status: u.aktiv ? 'active' : 'disabled' }));
 const inviteRows = pendingInvites
 .filter(i => isInternalRole(i.rolle))
 .map(i => {
 const expired = new Date(i.laeuft_ab_am) < new Date();
 return { ...i, _kind: 'invite', _status: expired ? 'expired' : 'invited' };
 });
 const all = [...userRows, ...inviteRows];
 const matchSearch = r => !search ||
 `${r.vorname || ''} ${r.nachname || ''} ${r.email}`.toLowerCase().includes(search.toLowerCase());
 const matchStatus = r => {
 if (internStatusFilter === 'all') return true;
 if (internStatusFilter === 'active_invited') return r._status === 'active' || r._status === 'invited' || r._status === 'expired';
 return r._status === internStatusFilter;
 };
 return all.filter(matchSearch).filter(matchStatus);
 }, [users, pendingInvites, roles, search, internStatusFilter]);

 const internSummary = useMemo(() => {
 const userRows = users.filter(u => isInternalRole(u.rolle));
 const inviteRows = pendingInvites.filter(i => isInternalRole(i.rolle));
 return {
 active: userRows.filter(u => u.aktiv).length,
 disabled: userRows.filter(u => !u.aktiv).length,
 invited: inviteRows.filter(i => new Date(i.laeuft_ab_am) >= new Date()).length,
 expired: inviteRows.filter(i => new Date(i.laeuft_ab_am) < new Date()).length,
 };
 }, [users, pendingInvites, roles]);

 // Legacy alias (Tab-Counter)
 const internalUsers = useMemo(() =>
 users.filter(u => isInternalRole(u.rolle))
 .filter(u => !search || `${u.vorname} ${u.nachname} ${u.email}`.toLowerCase().includes(search.toLowerCase()))
 , [users, roles, search]);

 const portalKundenFiltered = useMemo(() =>
 kunden
 .filter(k => statusFilter === 'all' || k.portal_status === statusFilter)
 .filter(k => segmentFilter === 'all' || k.segment === segmentFilter)
 .filter(k => !search || k.name?.toLowerCase().includes(search.toLowerCase()))
 , [kunden, statusFilter, segmentFilter, search]);

 const portalMAUsers = useMemo(() =>
 users.filter(u => ['portal_mitarbeiter', 'mitarbeiter'].includes(u.rolle))
 .filter(u => !search || `${u.vorname} ${u.nachname} ${u.email}`.toLowerCase().includes(search.toLowerCase()))
 , [users, search]);

 const maFiltered = useMemo(() => {
 let list = maData.mitarbeiter || [];
 if (maFilter === 'with') list = list.filter(m => m.user_id);
 if (maFilter === 'without') list = list.filter(m => !m.user_id);
 if (search) list = list.filter(m => `${m.vorname} ${m.nachname} ${m.barcode || ''} ${m.email || ''}`.toLowerCase().includes(search.toLowerCase()));
 return list;
 }, [maData, maFilter, search]);

 // ─── Summary counts ───
 const kundenSummary = useMemo(() => {
 const counts = { total: kunden.length, active: 0, invited: 0, none: 0, disabled: 0, expired: 0 };
 kunden.forEach(k => { counts[k.portal_status] = (counts[k.portal_status] || 0) + 1; });
 return counts;
 }, [kunden]);

 // ─── Bulk invite ───
 const selectedWithoutEmail = useMemo(() => {
 return [...selectedKunden].filter(id => {
 const k = kunden.find(c => c.id === id);
 return k && !k.kontakt_email;
 }).length;
 }, [selectedKunden, kunden]);

 const handleBulkInvite = async () => {
 setBulkInviting(true);
 try {
 const ids = [...selectedKunden];
 const result = await apiPost('/api/einladungen/bulk', { customer_ids: ids });
 if (result.error) throw new Error(result.error);
 setBulkResult(result);
 setBulkConfirmOpen(false);
 setSelectedKunden(new Set());
 load();
 } catch (err) {
 setBulkResult({ invited: [], skipped: [], errors: [{ reason: err.message }] });
 setBulkConfirmOpen(false);
 }
 setBulkInviting(false);
 };

 const toggleKundeSelection = (id) => {
 setSelectedKunden(prev => {
 const next = new Set(prev);
 next.has(id) ? next.delete(id) : next.add(id);
 return next;
 });
 };

 // ─── MA-Tab handlers ───
 const handleCreateUser = async (e) => {
 e.preventDefault();
 setCreateLoading(true);
 setCreateError('');
 try {
 const res = await apiPost('/api/admin/user-erstellen', {
 mitarbeiter_id: createModal.ma.ma_id,
 email: createForm.email,
 rolle: createForm.rolle,
 password: createForm.password,
 generate_pin: createForm.generate_pin,
 });
 if (res.error) throw new Error(res.error);
 setCreateModal(null);
 setCreateForm({ email: '', rolle: 'portal_mitarbeiter', password: '', generate_pin: true });
 load();
 } catch (err) { setCreateError(err.message); }
 setCreateLoading(false);
 };

 const handlePasswordReset = async (userId) => {
 const pw = prompt(t('userAdmin.maTab.enterNewPassword'));
 if (!pw || pw.length < 6) return;
 try {
 await apiPost(`/api/admin/user/${userId}/passwort-reset`, { password: pw });
 load();
 } catch { /* ignore */ }
 };

 const handleLinkMa = async (userId, maId) => {
 try {
 await apiPut(`/api/admin/users/${userId}/mapping`, { mitarbeiter_id: maId });
 setLinkDropdown(null);
 load();
 } catch { /* ignore */ }
 };

 const tabCounts = {
 intern: internalUsers.length,
 portal_kunden: `${kundenSummary.active}/${kunden.length}`,
 portal_ma: portalMAUsers.length,
 mitarbeiter: `${maData.summary?.with_account || 0}/${maData.summary?.total || 0}`,
 };

 const tabItems = [
 { key: 'intern', label: t('userAdmin.internalUsers'), count: tabCounts.intern },
 { key: 'portal_kunden', label: t('userAdmin.portalCustomers'), count: tabCounts.portal_kunden },
 { key: 'portal_ma', label: t('userAdmin.portalEmployees'), count: tabCounts.portal_ma },
 { key: 'rollen', label: t('userAdmin.rolesTab'), count: roles.length },
 { key: 'permissions', label: t('userAdmin.permissionsTab'), count: null },
 ];

 return (
 <div className="p-6">
 {/* Header */}
 <div className="flex items-center justify-between mb-5">
 <div>
 <h1 className="text-lg font-bold text-[var(--text-primary)]">{t('userAdmin.title')}</h1>
 <p className="text-xs text-[var(--text-secondary)] mt-0.5">{users.filter(u => u.aktiv).length} {t('userAdmin.activeUsers')}</p>
 </div>
 <button onClick={() => setInviteModal({})}
 className="bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium px-3.5 py-2 rounded-lg flex items-center gap-1.5 transition-colors">
 <UserPlus style={{ width: 14, height: 14 }} />
 {t('userAdmin.invite')}
 </button>
 </div>

 {/* Tabs */}
 <div className="flex gap-1 mb-4 bg-[var(--bg-inset)] p-1 rounded-lg w-fit border border-[var(--border-card)]">
 {tabItems.map(ti => (
 <button key={ti.key} onClick={() => { setTab(ti.key); setSearch(''); setStatusFilter('all'); setSegmentFilter('all'); setSelectedKunden(new Set()); }}
 className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tab === ti.key ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-[var(--text-primary)] font-semibold' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
 {ti.label} <span className="text-[var(--text-tertiary)] ml-0.5">({ti.count})</span>
 </button>
 ))}
 </div>

 {/* Search + Filters (hidden on rollen/permissions tabs) */}
 {tab !== 'rollen' && tab !== 'permissions' && (
 <div className="flex items-center gap-2 mb-3">
 <div className="relative flex-1 max-w-xs">
 <Search style={{ width: 14, height: 14 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
 <input type="text" value={search} onChange={e => setSearch(e.target.value)}
 placeholder={t('userAdmin.filters.search')}
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg pl-8 pr-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50" />
 </div>
 {tab === 'portal_kunden' && (
 <>
 <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
 className="bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50">
 <option value="all">{t('userAdmin.filters.all')} Status</option>
 <option value="active">{t('userAdmin.portalStatus.active')}</option>
 <option value="invited">{t('userAdmin.portalStatus.invited')}</option>
 <option value="none">{t('userAdmin.portalStatus.none')}</option>
 <option value="disabled">{t('userAdmin.portalStatus.disabled')}</option>
 </select>
 <select value={segmentFilter} onChange={e => setSegmentFilter(e.target.value)}
 className="bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50">
 <option value="all">{t('userAdmin.filters.all')} Segment</option>
 {['A', 'B', 'C'].map(s => <option key={s} value={s}>{s}</option>)}
 </select>
 </>
 )}
 {tab === 'mitarbeiter' && (
 <div className="flex gap-1 bg-[var(--bg-inset)] p-0.5 rounded-lg border border-[var(--border-card)]">
 {[['all', t('userAdmin.filters.all')], ['with', t('userAdmin.maTab.withAccount')], ['without', t('userAdmin.maTab.withoutAccount')]].map(([f, label]) => (
 <button key={f} onClick={() => setMaFilter(f)}
 className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${maFilter === f ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
 {label}
 </button>
 ))}
 </div>
 )}
 {tab === 'intern' && (
 <select value={internStatusFilter} onChange={e => setInternStatusFilter(e.target.value)}
 className="bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50">
 <option value="active_invited">{t('userAdmin.filterActiveInvited') || 'Aktiv + Eingeladen'}</option>
 <option value="all">{t('userAdmin.filters.all') || 'Alle'}</option>
 <option value="active">{t('userAdmin.statusActive')}</option>
 <option value="invited">{t('userAdmin.portalStatus.invited')}</option>
 <option value="expired">{t('userAdmin.portalStatus.expired') || 'Abgelaufen'}</option>
 <option value="disabled">{t('userAdmin.statusDisabled')}</option>
 </select>
 )}
 </div>
 )}

 {/* Intern Summary */}
 {tab === 'intern' && (
 <div className="text-[11px] text-[var(--text-secondary)] mb-3">
 <span className="text-emerald-500">{internSummary.active} {t('userAdmin.statusActive').toLowerCase()}</span>
 {' · '}<span className="text-primary-500">{internSummary.invited} {t('userAdmin.portalStatus.invited').toLowerCase()}</span>
 {internSummary.expired > 0 && <> {' · '}<span className="text-amber-500">{internSummary.expired} {(t('userAdmin.portalStatus.expired') || 'abgelaufen').toLowerCase()}</span></>}
 {internSummary.disabled > 0 && <> {' · '}<span className="text-[var(--text-secondary)]">{internSummary.disabled} {t('userAdmin.statusDisabled').toLowerCase()}</span></>}
 </div>
 )}

 {/* Portal-Kunden Summary */}
 {tab === 'portal_kunden' && (
 <div className="text-[11px] text-[var(--text-secondary)] mb-3">
 {kunden.length} {t('userAdmin.customers')} · <span className="text-emerald-500">{kundenSummary.active} {t('userAdmin.portalStatus.active').toLowerCase()}</span> · <span className="text-primary-500">{kundenSummary.invited} {t('userAdmin.portalStatus.invited').toLowerCase()}</span> · {kundenSummary.none} {t('userAdmin.withoutPortal')}
 </div>
 )}

 {/* Mitarbeiter Summary */}
 {tab === 'mitarbeiter' && maData.summary && (
 <div className="text-[11px] text-[var(--text-secondary)] mb-3">
 {maData.summary.total || 0} {t('userAdmin.maTab.employees')} · <span className="text-emerald-500">{maData.summary.with_account || 0} {t('userAdmin.maTab.withAccount')}</span> · {maData.summary.without_account || 0} {t('userAdmin.maTab.withoutAccount')}
 </div>
 )}

 {/* Content */}
 {loading ? (
 <div className="flex justify-center py-12"><div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : tab === 'intern' ? (
 /* ─── INTERNAL USERS TABLE ─── */
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl overflow-visible">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-[var(--border-card)]">
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">User</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.roleHeader')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.maLinkCol')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.status')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.lastLogin')}</th>
 <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider" />
 </tr>
 </thead>
 <tbody>
 {internalRows.map(row => {
 const isInvite = row._kind === 'invite';
 const status = row._status;
 const statusConfig = {
 active: { dot: 'bg-emerald-500', text: 'text-emerald-500', label: t('userAdmin.statusActive') },
 invited: { dot: 'bg-primary-500', text: 'text-primary-600 dark:text-primary-400', label: t('userAdmin.portalStatus.invited') },
 expired: { dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', label: t('userAdmin.portalStatus.expired') || 'Abgelaufen' },
 disabled: { dot: 'bg-[var(--text-muted,#9CA3AF)]', text: 'text-[var(--text-secondary,#4B5563)]', label: t('userAdmin.statusDisabled') },
 }[status];
 const rowKey = `${row._kind}-${row.id}`;
 return (
 <tr key={rowKey}
 onClick={() => !isInvite && handleUserClick(row)}
 className={`border-b border-[var(--border-card)] hover:bg-[var(--bg-inset)] transition-colors ${isInvite ? '' : 'cursor-pointer'} ${status === 'disabled' ? 'opacity-50' : ''}`}>
 <td className="px-4 py-3">
 <div className="flex items-center gap-3">
 <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isInvite ? 'bg-[var(--bg-inset)] border border-dashed border-primary-500/40' : 'bg-gradient-to-br from-primary-500 to-purple-600'}`}>
 {isInvite ? <Mail style={{ width: 12, height: 12 }} className="text-primary-500" />
 : <span className="text-[10px] font-bold text-white">{(row.vorname?.[0] || '') + (row.nachname?.[0] || '')}</span>}
 </div>
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">
 {isInvite ? row.email : `${row.vorname || ''} ${row.nachname || ''}`}
 </div>
 <div className="text-[10px] text-[var(--text-secondary)]">
 {isInvite
 ? `${t('userAdmin.invitedAt') || 'Eingeladen am'} ${new Date(row.created_at).toLocaleDateString('de-AT')}${row.eingeladen_von_name ? ` · ${t('userAdmin.invitedBy') || 'von'} ${row.eingeladen_von_name}` : ''}`
 : row.email}
 </div>
 </div>
 </div>
 </td>
 <td className="px-4 py-3">
 <div className="flex items-center gap-1.5 flex-wrap">
 <RoleBadge rolle={row.rolle} rolesMeta={roles} />
 {!isInvite && usersWithOverrides.has(row.id) && (
 <span title={t('userAdmin.overrides.badgeHint')}
 className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
 <SlidersHorizontal style={{ width: 10, height: 10 }} />
 {t('userAdmin.overrides.badge')}
 </span>
 )}
 </div>
 </td>
 <td className="px-4 py-3">
 {isInvite ? (
 <span className="text-[11px] text-[var(--text-tertiary)]">—</span>
 ) : row.mitarbeiter_name ? (
 <span className="inline-flex items-center gap-1 text-[11px] text-emerald-500 font-medium">
 <UserCheck style={{ width: 12, height: 12 }} />
 {row.mitarbeiter_name}
 </span>
 ) : (
 <span className="inline-flex items-center gap-1 text-[11px] text-amber-500">
 <AlertCircle style={{ width: 12, height: 12 }} />
 {t('userAdmin.notLinked')}
 </span>
 )}
 </td>
 <td className="px-4 py-3">
 <div>
 <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${statusConfig.text}`}>
 <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
 {statusConfig.label}
 </span>
 {isInvite && row.laeuft_ab_am && (
 <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
 {status === 'expired'
 ? (t('userAdmin.expiredOn') || 'Abgelaufen am') + ' ' + new Date(row.laeuft_ab_am).toLocaleDateString('de-AT')
 : (t('userAdmin.expiresIn') || 'läuft ab in') + ' ' + Math.max(0, Math.ceil((new Date(row.laeuft_ab_am) - new Date()) / 86400000)) + 'd'}
 </div>
 )}
 </div>
 </td>
 <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">
 {isInvite ? '—' : (row.letzter_login ? new Date(row.letzter_login).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : t('userAdmin.never'))}
 </td>
 <td className="px-4 py-3 text-right">
 <div className="flex items-center justify-end gap-1.5">
 {!isInvite && !row.mitarbeiter_id && row.aktiv && (
 <div className="relative">
 <button onClick={(e) => { e.stopPropagation(); setLinkDropdown(linkDropdown === row.id ? null : row.id); }}
 className="inline-flex items-center gap-1 text-[10px] font-medium text-[#338edc] hover:text-[#2b7bc4] transition-colors px-1.5 py-1 rounded"
 title={t('userAdmin.linkMa')}>
 <Link2 style={{ width: 12, height: 12 }} />
 </button>
 {linkDropdown === row.id && (
 <div className="absolute right-0 top-full mt-1 w-56 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg shadow-sm z-50 max-h-48 overflow-y-auto py-1" onClick={e => e.stopPropagation()}>
 <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">{t('userAdmin.linkMa')}</div>
 {maVerfuegbar.length > 0 ? maVerfuegbar.map(m => {
 const isLinked = !!m.linked_user_id;
 return (
 <button key={m.id} onClick={() => !isLinked && handleLinkMa(row.id, m.id)}
 disabled={isLinked}
 className={`w-full px-3 py-2 text-left text-xs transition-colors ${isLinked ? 'text-[var(--text-muted)] cursor-not-allowed' : 'text-[var(--text-primary)] hover:bg-[var(--bg-inset)]'}`}
 title={isLinked ? `${t('userAdmin.alreadyLinkedTo', 'Bereits verknüpft mit')}: ${m.linked_user_name || m.linked_user_email}` : ''}>
 {m.name} {m.kuerzel ? <span className="text-[var(--text-tertiary)]">({m.kuerzel})</span> : ''}
 {isLinked && <span className="text-[var(--text-muted)] ml-1">· {m.linked_user_email}</span>}
 </button>
 );
 }) : (
 <div className="px-3 py-2 text-xs text-[var(--text-tertiary)]">{t('userAdmin.noUnlinkedMa')}</div>
 )}
 </div>
 )}
 </div>
 )}
 {/* #981 Aktions-Dropdown */}
 <div className="relative">
 <button onClick={(e) => { e.stopPropagation(); setActionMenu(actionMenu?.key === rowKey ? null : { key: rowKey, kind: row._kind, id: row.id, row }); }}
 className="inline-flex items-center justify-center w-7 h-7 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
 title={t('common.actions') || 'Aktionen'}>
 <MoreHorizontal style={{ width: 14, height: 14 }} />
 </button>
 {actionMenu?.key === rowKey && (
 <div className="absolute right-0 top-full mt-1 w-52 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg shadow-lg z-50 py-1" onClick={e => e.stopPropagation()}>
 {isInvite ? (
 <>
 <button onClick={() => handleResendInvite(row.id)}
 className="w-full px-3 py-2 text-left text-xs text-[var(--text-primary)] hover:bg-[var(--bg-inset)] flex items-center gap-2">
 <RefreshCw style={{ width: 12, height: 12 }} className="text-[#338edc]" />
 {t('userAdmin.resendInvite') || 'Einladung erneut senden'}
 </button>
 <button onClick={() => { setActionMenu(null); setDeleteInviteConfirm({ id: row.id, email: row.email }); }}
 className="w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-red-500/10 flex items-center gap-2">
 <Trash2 style={{ width: 12, height: 12 }} />
 {t('userAdmin.revokeInvite') || 'Einladung widerrufen'}
 </button>
 </>
 ) : (
 <>
 <button onClick={() => { setActionMenu(null); handleUserClick(row); }}
 className="w-full px-3 py-2 text-left text-xs text-[var(--text-primary)] hover:bg-[var(--bg-inset)] flex items-center gap-2">
 <Pencil style={{ width: 12, height: 12 }} className="text-[#338edc]" />
 {t('common.edit') || 'Bearbeiten'}
 </button>
 <button onClick={() => { setActionMenu(null); setPwResetTarget({ id: row.id, email: row.email, password: '' }); }}
 className="w-full px-3 py-2 text-left text-xs text-[var(--text-primary)] hover:bg-[var(--bg-inset)] flex items-center gap-2">
 <KeyRound style={{ width: 12, height: 12 }} className="text-amber-500" />
 {t('userAdmin.resetPassword') || 'Passwort zurücksetzen'}
 </button>
 <button onClick={() => handleToggleAktiv(row)}
 className="w-full px-3 py-2 text-left text-xs text-[var(--text-primary)] hover:bg-[var(--bg-inset)] flex items-center gap-2">
 {row.aktiv
 ? <><Ban style={{ width: 12, height: 12 }} className="text-amber-500" />{t('userAdmin.deactivate') || 'Deaktivieren'}</>
 : <><Power style={{ width: 12, height: 12 }} className="text-emerald-500" />{t('userAdmin.activate') || 'Aktivieren'}</>}
 </button>
 {!row.aktiv && !row.mitarbeiter_id && !row.kunde_id && !row.letzter_login && (
 <button onClick={() => { setActionMenu(null); setDeleteUserConfirm({ id: row.id, email: row.email, vorname: row.vorname, nachname: row.nachname }); }}
 className="w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-red-500/10 flex items-center gap-2 border-t border-[var(--border-card)] mt-1 pt-2">
 <Trash2 style={{ width: 12, height: 12 }} />
 {t('userAdmin.deleteHard') || 'Endgültig löschen'}
 </button>
 )}
 </>
 )}
 </div>
 )}
 </div>
 {!isInvite && <ChevronRight style={{ width: 14, height: 14 }} className="text-[var(--text-tertiary)]" />}
 </div>
 </td>
 </tr>
 );
 })}
 {internalRows.length === 0 && (
 <tr><td colSpan="6" className="px-4 py-8 text-center text-xs text-[var(--text-tertiary)]">{t('userAdmin.noResults')}</td></tr>
 )}
 </tbody>
 </table>
 </div>

 ) : tab === 'portal_kunden' ? (
 /* ─── PORTAL KUNDEN TABLE ─── */
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl overflow-hidden">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-[var(--border-card)]">
 <th className="text-left px-4 py-2.5 w-8" />
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.customer')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">E-Mail</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.portalStatusHeader')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.lastLogin')}</th>
 <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.actions')}</th>
 </tr>
 </thead>
 <tbody>
 {portalKundenFiltered.map(k => {
 const portalUsers = k.portal_users || [];
 return (
 <Fragment key={k.id}>
 <tr className="border-b border-[var(--border-card)] hover:bg-[var(--bg-inset)] transition-colors">
 <td className="px-4 py-3">
 {k.kontakt_email && (
 <input type="checkbox" checked={selectedKunden.has(k.id)} onChange={() => toggleKundeSelection(k.id)}
 className="w-3.5 h-3.5 rounded border-[var(--border-card)] text-[#338edc] focus:ring-[#338edc]/30" />
 )}
 </td>
 <td className="px-4 py-3">
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">{k.name}</div>
 <div className="flex items-center gap-1.5">
 {k.segment && <span className="text-[10px] text-[var(--text-tertiary)]">Segment {k.segment}</span>}
 {portalUsers.length > 0 && (
 <span className="text-[10px] text-[var(--text-tertiary)]">· {portalUsers.length} {portalUsers.length === 1 ? 'User' : 'Users'}</span>
 )}
 </div>
 </div>
 </td>
 <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">
 {k.kontakt_email || (
 <span className="text-amber-500 dark:text-amber-400">{t('userAdmin.noEmail')}</span>
 )}
 </td>
 <td className="px-4 py-3">
 <PortalStatusBadge status={k.portal_status} t={t} />
 {k.portal_status === 'invited' && k.einladung_ablauf && (
 <span className="ml-1.5 text-[10px] text-[var(--text-tertiary)]">
 ({t('userAdmin.expiresIn')} {Math.max(0, Math.ceil((new Date(k.einladung_ablauf) - new Date()) / 86400000))}d)
 </span>
 )}
 </td>
 <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">
 {k.letzter_login ? new Date(k.letzter_login).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}
 </td>
 <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
 {(k.portal_status === 'invited' || k.portal_status === 'expired') && k.einladung_id && (
 <button onClick={async () => {
 const res = await apiPost(`/api/einladungen/${k.einladung_id}/resend`);
 if (res.sent) setSuccess(t('userAdmin.resendSuccess'));
 else setSuccess(null);
 }}
 className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors">
 <RefreshCw style={{ width: 11, height: 11 }} />
 {t('userAdmin.resend')}
 </button>
 )}
 {k.kontakt_email && (
 <button onClick={() => setInviteModal({ kunde: k })}
 className="inline-flex items-center gap-1 text-[10px] font-medium text-[#338edc] hover:text-[#2b7bc4] transition-colors">
 <Mail style={{ width: 12, height: 12 }} />
 {t('userAdmin.invite')}
 </button>
 )}
 </td>
 </tr>
 {/* Sub-rows: alle Portal-User dieses Kunden */}
 {portalUsers.map(pu => (
 <tr key={`user-${pu.id}`} className="border-b border-[var(--border-card)] bg-[var(--bg-inset)]/50">
 <td className="px-4 py-2" />
 <td className="px-4 py-2 pl-10">
 <div className="flex items-center gap-2">
 <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-[#338edc]/10">
 <span className="text-[8px] font-bold text-[#338edc]">{pu.email?.[0]?.toUpperCase() || 'U'}</span>
 </div>
 <span className="text-[11px] text-[var(--text-primary)]">{pu.email}</span>
 </div>
 </td>
 <td className="px-4 py-2" />
 <td className="px-4 py-2">
 <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${pu.aktiv ? 'text-emerald-500' : 'text-[var(--text-secondary,#4B5563)]'}`}>
 <span className={`w-1.5 h-1.5 rounded-full ${pu.aktiv ? 'bg-emerald-500' : 'bg-[var(--text-muted,#9CA3AF)]'}`} />
 {pu.aktiv ? t('userAdmin.statusActive') : t('userAdmin.statusDisabled')}
 </span>
 </td>
 <td className="px-4 py-2 text-[11px] text-[var(--text-secondary)]">
 {pu.letzter_login ? new Date(pu.letzter_login).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}
 </td>
 <td className="px-4 py-2 text-right flex items-center justify-end gap-2">
 {pu.aktiv && (
 <button onClick={() => handleImpersonate(pu.id)}
 title={t('userAdmin.impersonate')}
 className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors">
 <LogIn style={{ width: 12, height: 12 }} />
 {t('userAdmin.impersonate')}
 </button>
 )}
 <button onClick={() => setDeleteConfirm({ id: pu.id, email: pu.email, kundeName: k.name })}
 title={t('userAdmin.deletePortalUser')}
 className="inline-flex items-center gap-1 text-[10px] font-medium text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors">
 <Trash2 style={{ width: 12, height: 12 }} />
 </button>
 <button onClick={() => {
 const user = users.find(u => u.id === pu.id);
 if (user) handleUserClick(user);
 }}
 className="text-[10px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 <ChevronRight style={{ width: 14, height: 14 }} className="inline" />
 </button>
 </td>
 </tr>
 ))}
 </Fragment>
 );
 })}
 {portalKundenFiltered.length === 0 && (
 <tr><td colSpan="6" className="px-4 py-8 text-center text-xs text-[var(--text-tertiary)]">{t('userAdmin.noResults')}</td></tr>
 )}
 </tbody>
 </table>
 </div>

 ) : tab === 'rollen' ? (
 /* ─── ROLLEN TAB ─── */
 <RolesTab roles={roles} users={users} onReload={load} t={t} />
 ) : tab === 'permissions' ? (
 /* ─── PERMISSIONS MATRIX TAB ─── */
 <PermissionsMatrixTab roles={roles} t={t} />
 ) : tab === 'portal_ma' ? (
 /* ─── PORTAL MA TAB ─── */
 portalMAUsers.length > 0 ? (
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl overflow-hidden">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-[var(--border-card)]">
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">User</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.status')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.lastLogin')}</th>
 </tr>
 </thead>
 <tbody>
 {portalMAUsers.map(u => (
 <tr key={u.id} onClick={() => handleUserClick(u)}
 className="border-b border-[var(--border-card)] hover:bg-[var(--bg-inset)] cursor-pointer transition-colors">
 <td className="px-4 py-3">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-indigo-500 to-primary-600">
 <span className="text-[10px] font-bold text-white">{(u.vorname?.[0] || '') + (u.nachname?.[0] || '')}</span>
 </div>
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">{u.vorname} {u.nachname}</div>
 <div className="text-[10px] text-[var(--text-secondary)]">{u.email}</div>
 </div>
 </div>
 </td>
 <td className="px-4 py-3">
 <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${u.aktiv ? 'text-emerald-500' : 'text-[var(--text-secondary,#4B5563)]'}`}>
 <span className={`w-1.5 h-1.5 rounded-full ${u.aktiv ? 'bg-emerald-500' : 'bg-[var(--text-muted,#9CA3AF)]'}`} />
 {u.aktiv ? t('userAdmin.statusActive') : t('userAdmin.statusDisabled')}
 </span>
 </td>
 <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">
 {u.letzter_login ? new Date(u.letzter_login).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : t('userAdmin.never')}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 ) : (
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl p-12 text-center">
 <User style={{ width: 32, height: 32 }} className="mx-auto text-[var(--text-tertiary)] mb-3" />
 <p className="text-sm font-medium text-[var(--text-secondary)]">{t('userAdmin.maPortalEmpty')}</p>
 <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('userAdmin.maPortalComingSoon')}</p>
 </div>
 )

 ) : (
 /* ─── MITARBEITER TAB ─── */
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl overflow-hidden">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-[var(--border-card)]">
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.maTab.name')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.maTab.department')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.maTab.userAccount')}</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">E-Mail</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">OMS</th>
 <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.lastLogin')}</th>
 <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('userAdmin.actions')}</th>
 </tr>
 </thead>
 <tbody>
 {maFiltered.map(m => (
 <tr key={m.ma_id} className="border-b border-[var(--border-card)] hover:bg-[var(--bg-inset)] transition-colors">
 <td className="px-4 py-3">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-primary-500 to-indigo-600">
 <span className="text-[10px] font-bold text-white">{(m.vorname?.[0] || '') + (m.nachname?.[0] || '')}</span>
 </div>
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">{m.vorname} {m.nachname}</div>
 <div className="text-[10px] text-[var(--text-tertiary)]">{m.barcode || '–'}</div>
 </div>
 </div>
 </td>
 <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">{m.kostenstelle || '–'}</td>
 <td className="px-4 py-3">
 {m.user_id ? (
 <span className="inline-flex items-center gap-1.5">
 <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
 <RoleBadge rolle={m.user_rolle} rolesMeta={roles} />
 </span>
 ) : (
 <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-tertiary)]">
 <span className="w-1.5 h-1.5 rounded-full bg-[var(--text-muted,#9CA3AF)] dark:bg-[var(--text-secondary,#4B5563)]" />
 {t('userAdmin.maTab.noAccount')}
 </span>
 )}
 </td>
 <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">{m.email || '–'}</td>
 <td className="px-4 py-3">
 {m.oms_username ? (
 <span className="inline-flex items-center gap-1 text-[11px] text-emerald-500">
 <Monitor style={{ width: 12, height: 12 }} />
 {m.oms_username}
 </span>
 ) : (
 <span className="text-[11px] text-[var(--text-tertiary)]">–</span>
 )}
 </td>
 <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">
 {m.letzter_login ? new Date(m.letzter_login).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}
 </td>
 <td className="px-4 py-3 text-right">
 {m.user_id ? (
 <div className="flex items-center justify-end gap-2">
 <button onClick={() => handlePasswordReset(m.user_id)}
 className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
 title={t('userAdmin.detail.resetPassword')}>
 <KeyRound style={{ width: 12, height: 12 }} />
 </button>
 <button onClick={() => {
 const user = users.find(u => u.id === m.user_id);
 if (user) handleUserClick(user);
 }}
 className="text-[10px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 <ChevronRight style={{ width: 14, height: 14 }} />
 </button>
 </div>
 ) : (
 <button onClick={() => { setCreateForm({ email: '', rolle: 'portal_mitarbeiter', password: '', generate_pin: true }); setCreateError(''); setCreateModal({ ma: m }); }}
 className="inline-flex items-center gap-1 text-[10px] font-medium text-[#338edc] hover:text-[#2b7bc4] transition-colors">
 <UserPlus style={{ width: 12, height: 12 }} />
 {t('userAdmin.maTab.createUser')}
 </button>
 )}
 </td>
 </tr>
 ))}
 {maFiltered.length === 0 && (
 <tr><td colSpan="7" className="px-4 py-8 text-center text-xs text-[var(--text-tertiary)]">{t('userAdmin.noResults')}</td></tr>
 )}
 </tbody>
 </table>
 </div>
 )}

 {/* Bulk Action Bar */}
 {selectedKunden.size > 0 && (
 <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl shadow-sm px-5 py-3 flex items-center gap-4 z-40">
 <span className="text-xs text-[var(--text-primary)] font-medium">{selectedKunden.size} {t('userAdmin.selected')}</span>
 <button onClick={() => setBulkConfirmOpen(true)}
 className="bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors">
 <Mail style={{ width: 14, height: 14 }} />
 {t('userAdmin.bulkInvite')}
 </button>
 <button onClick={() => setSelectedKunden(new Set())}
 className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 {t('common.cancel')}
 </button>
 </div>
 )}

 {/* Bulk Confirm Dialog */}
 {bulkConfirmOpen && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !bulkInviting && setBulkConfirmOpen(false)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('userAdmin.bulkConfirm.title')}</h3>
 <button onClick={() => setBulkConfirmOpen(false)} disabled={bulkInviting} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <div className="p-5 space-y-3">
 <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{t('userAdmin.bulkConfirm.text', { count: selectedKunden.size })}</p>
 {selectedWithoutEmail > 0 && (
 <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
 <AlertCircle style={{ width: 14, height: 14 }} className="text-amber-500 flex-shrink-0" />
 <span className="text-xs text-amber-600 dark:text-amber-400">{t('userAdmin.bulkConfirm.skippedNoEmail', { count: selectedWithoutEmail })}</span>
 </div>
 )}
 <div className="flex justify-end gap-2 pt-2">
 <button onClick={() => setBulkConfirmOpen(false)} disabled={bulkInviting}
 className="px-4 py-2 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 {t('common.cancel')}
 </button>
 <button onClick={handleBulkInvite} disabled={bulkInviting}
 className="bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50">
 {bulkInviting ? (
 <span className="flex items-center gap-1.5"><span className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />...</span>
 ) : (
 <><Mail style={{ width: 14, height: 14 }} />{t('userAdmin.bulkConfirm.send')}</>
 )}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Bulk Result Dialog */}
 {bulkResult && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setBulkResult(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('userAdmin.bulkResult.title')}</h3>
 <button onClick={() => setBulkResult(null)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <div className="p-5 space-y-2">
 {bulkResult.invited?.length > 0 && (
 <div className="flex items-center gap-2 text-xs">
 <Check style={{ width: 14, height: 14 }} className="text-emerald-500" />
 <span className="text-emerald-600 dark:text-emerald-400 font-medium">{t('userAdmin.bulkResult.invited', { count: bulkResult.invited.length })}</span>
 </div>
 )}
 {(() => {
 const noEmail = bulkResult.skipped?.filter(s => s.reason === 'no_email') || [];
 const existing = bulkResult.skipped?.filter(s => s.reason === 'already_active' || s.reason === 'already_invited') || [];
 const conflict = bulkResult.skipped?.filter(s => s.reason === 'email_conflict') || [];
 return (
 <>
 {noEmail.length > 0 && (
 <div className="flex items-center gap-2 text-xs">
 <AlertCircle style={{ width: 14, height: 14 }} className="text-amber-500" />
 <span className="text-amber-600 dark:text-amber-400">{t('userAdmin.bulkResult.skippedNoEmail', { count: noEmail.length })}</span>
 </div>
 )}
 {existing.length > 0 && (
 <div className="flex items-center gap-2 text-xs">
 <AlertCircle style={{ width: 14, height: 14 }} className="text-amber-500" />
 <span className="text-amber-600 dark:text-amber-400">{t('userAdmin.bulkResult.skippedExisting', { count: existing.length })}</span>
 </div>
 )}
 {conflict.length > 0 && (
 <div className="flex items-center gap-2 text-xs">
 <AlertCircle style={{ width: 14, height: 14 }} className="text-amber-500" />
 <span className="text-amber-600 dark:text-amber-400">{t('userAdmin.bulkResult.skippedConflict', { count: conflict.length })}</span>
 </div>
 )}
 </>
 );
 })()}
 {bulkResult.errors?.length > 0 && (
 <div className="flex items-center gap-2 text-xs">
 <X style={{ width: 14, height: 14 }} className="text-red-500" />
 <span className="text-red-600 dark:text-red-400">{t('userAdmin.bulkResult.errors', { count: bulkResult.errors.length })}</span>
 </div>
 )}
 <div className="flex justify-end pt-3">
 <button onClick={() => setBulkResult(null)}
 className="bg-[var(--bg-inset)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] text-xs font-medium px-4 py-2 rounded-lg transition-colors">
 {t('common.close')}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Delete Portal User Confirm */}
 {deleteConfirm && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !deleting && setDeleteConfirm(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('userAdmin.deletePortalUser')}</h3>
 <button onClick={() => setDeleteConfirm(null)} disabled={deleting} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <div className="p-5 space-y-3">
 <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
 {t('userAdmin.deleteConfirm', { email: deleteConfirm.email, kunde: deleteConfirm.kundeName })}
 </p>
 <div className="flex justify-end gap-2 pt-2">
 <button onClick={() => setDeleteConfirm(null)} disabled={deleting}
 className="px-4 py-2 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 {t('common.cancel')}
 </button>
 <button onClick={handleDeletePortalUser} disabled={deleting}
 className="bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50">
 {deleting ? (
 <span className="flex items-center gap-1.5"><span className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />...</span>
 ) : (
 <><Trash2 style={{ width: 14, height: 14 }} />{t('userAdmin.deletePortalUser')}</>
 )}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Create User Modal (MA-Tab) */}
 {createModal && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !createLoading && setCreateModal(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('userAdmin.maTab.createUserFor')} {createModal.ma.vorname} {createModal.ma.nachname}</h3>
 <button onClick={() => setCreateModal(null)} disabled={createLoading} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <form onSubmit={handleCreateUser} className="p-5 space-y-3">
 {createError && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-500">{createError}</div>}
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">E-Mail</label>
 <input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} required
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50 focus:ring-1 focus:ring-[#338edc]/30"
 placeholder="name@weship.eu" />
 </div>
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.roleHeader')}</label>
 <select value={createForm.rolle} onChange={e => setCreateForm(f => ({ ...f, rolle: e.target.value }))}
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50">
 <option value="portal_mitarbeiter">Portal Mitarbeiter</option>
 {roles.filter(r => r.is_internal).map(r => <option key={r.rolle} value={r.rolle}>{r.label_de}</option>)}
 </select>
 </div>
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('userAdmin.maTab.password')}</label>
 <input type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} required minLength={6}
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50 focus:ring-1 focus:ring-[#338edc]/30"
 placeholder={t('userAdmin.maTab.passwordPlaceholder')} autoComplete="new-password" />
 </div>
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="checkbox" checked={createForm.generate_pin} onChange={e => setCreateForm(f => ({ ...f, generate_pin: e.target.checked }))}
 className="w-3.5 h-3.5 rounded border-[var(--border-card)] text-[#338edc] focus:ring-[#338edc]/30" />
 <span className="text-xs text-[var(--text-secondary)]">{t('userAdmin.maTab.generatePin')}</span>
 </label>
 <button type="submit" disabled={createLoading}
 className="w-full bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50">
 {createLoading ? '...' : t('userAdmin.maTab.createUser')}
 </button>
 </form>
 </div>
 </div>
 )}

 {/* Invite Modal */}
 <InviteModal
 open={!!inviteModal}
 onClose={() => setInviteModal(null)}
 tab={tab}
 kunden={kunden}
 presetKunde={inviteModal?.kunde || null}
 rolesMeta={roles}
 onSuccess={() => { setStatusFilter('all'); load(); }}
 t={t}
 />

 {/* #981: Hard-Delete User Confirm */}
 {deleteUserConfirm && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDeleteUserConfirm(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('userAdmin.deleteHard') || 'Endgültig löschen'}</h3>
 <button onClick={() => setDeleteUserConfirm(null)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <div className="p-5 space-y-3">
 <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
 {t('userAdmin.deleteHardConfirm', { email: deleteUserConfirm.email }) || `Soll der User ${deleteUserConfirm.email} wirklich endgültig aus der DB gelöscht werden? Diese Aktion ist nicht rückgängig zu machen.`}
 </p>
 <div className="flex justify-end gap-2 pt-2">
 <button onClick={() => setDeleteUserConfirm(null)}
 className="px-4 py-2 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 {t('common.cancel')}
 </button>
 <button onClick={handleDeleteUserHard}
 className="bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors">
 <Trash2 style={{ width: 14, height: 14 }} />{t('userAdmin.deleteHard') || 'Endgültig löschen'}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* #981: Delete Invite Confirm */}
 {deleteInviteConfirm && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDeleteInviteConfirm(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('userAdmin.revokeInvite') || 'Einladung widerrufen'}</h3>
 <button onClick={() => setDeleteInviteConfirm(null)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <div className="p-5 space-y-3">
 <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
 {t('userAdmin.revokeInviteConfirm', { email: deleteInviteConfirm.email }) || `Einladung an ${deleteInviteConfirm.email} widerrufen? Der Einladungslink wird ungültig.`}
 </p>
 <div className="flex justify-end gap-2 pt-2">
 <button onClick={() => setDeleteInviteConfirm(null)}
 className="px-4 py-2 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 {t('common.cancel')}
 </button>
 <button onClick={handleDeleteInvite}
 className="bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors">
 <Trash2 style={{ width: 14, height: 14 }} />{t('userAdmin.revokeInvite') || 'Widerrufen'}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* #981: Reset Password Modal */}
 {pwResetTarget && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setPwResetTarget(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('userAdmin.resetPassword') || 'Passwort zurücksetzen'}</h3>
 <button onClick={() => setPwResetTarget(null)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><X style={{ width: 16, height: 16 }} /></button>
 </div>
 <div className="p-5 space-y-3">
 <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
 {t('userAdmin.resetPasswordHint', { email: pwResetTarget.email }) || `Neues Passwort für ${pwResetTarget.email} setzen (mind. 6 Zeichen):`}
 </p>
 <input type="text" autoFocus
 value={pwResetTarget.password || ''}
 onChange={e => setPwResetTarget({ ...pwResetTarget, password: e.target.value })}
 onKeyDown={e => { if (e.key === 'Enter') handleResetPassword(); }}
 placeholder="••••••••"
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 <div className="flex justify-end gap-2 pt-2">
 <button onClick={() => setPwResetTarget(null)}
 className="px-4 py-2 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 {t('common.cancel')}
 </button>
 <button onClick={handleResetPassword}
 className="bg-[#338edc] hover:bg-[#2d7bc4] text-white text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors">
 <KeyRound style={{ width: 14, height: 14 }} />{t('userAdmin.resetPassword') || 'Zurücksetzen'}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* #981: Action Feedback Toast */}
 {actionFeedback && (
 <div className="fixed bottom-6 right-6 z-[100] bg-[var(--text-primary)] text-white text-xs font-medium px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
 <Check style={{ width: 14, height: 14 }} />
 <span>{actionFeedback}</span>
 </div>
 )}

 {/* User Detail Slide-Over */}
 {selectedUser && (
 <UserDetailSlideOver
 user={selectedUser}
 rolesMeta={roles}
 permissions={selectedPerms}
 standorte={standorte}
 onClose={() => setSelectedUser(null)}
 onUpdate={() => { load(); }}
 t={t}
 />
 )}
 </div>
 );
}
